import * as R from 'ramda'

import { builtinEffects as editEffects } from 'coact/saga/editSaga'

import buildSkuName from '~/src/models/modules/buildSkuName.js'

import mobileBarcodeValidator from '~/src/invoice/twInvoice/validator/mobileBarcode.js'
// Straight at the validator, not via src/consumers: the barrel reaches the
// entity, which is server-side.
import mobilePhoneValidator from '~/src/consumers/data/consumers/validator/mobilePhone.js'

import findProductPrice from './findProductPrice.js'
import lookupConsumer, { refreshBalances } from './lookupConsumer.js'
import computeAmount from './computeAmount.js'
import computeEarnPoints from './computeEarnPoints.js'
import autoSelect from './autoSelect.js'
import invalidInvoice from './invalidInvoice.js'
import saveAndPrintInvoice from './saveAndPrintInvoice.js'
import saveAndPrintInvoiceOnPos from './saveAndPrintInvoiceOnPos.js'
import Cashier from './Cashier.jsx'
import {
  pointsBalance,
  creditBalance,
  reloadBalances,
} from './memberAdornments.jsx'

// A 折抵 field: a whole number, never negative. Returning a message aborts the
// save (coact saga/editSaga), which is what we want for malformed input.
// The one vendor there is, and the key its snapshot is stored under. A second
// gets its own section and its own key; the paths below name the vendor
// outright, so no section can render another platform's member id.
const SHOPLINE = 'shopline'

const spendable = label => value => {
  if (value === null || value === undefined || value === '') return null
  if (Number.isInteger(value) && value >= 0) return null

  return `${label}請填 0 或正整數`
}

export default {
  autofill: {
    pricebook: {
      src: 'id',
      fill: ({ key }) => ({ key }),
    },
    warehouse: {
      src: 'id',
      fill: ({ key, name }) => ({ key, name }),
    },
    '': [
      {
        src: 'consumer.id',
        fill: doc => {
          if (R.isNil(doc)) {
            return {
              mobileBarcode: null,
              consumer: { name: null, phone: null },
            }
          }

          const { name, phone, mobileBarcode } = doc
          return { mobileBarcode, consumer: { name, phone } }
        },
      },
    ],
    // consumer: {
    //   src: 'id',
    //   fill: ({ name, phone, mobileBarcode }) => ({ name, phone, mobileBarcode }),
    // },
    'items.0': {
      src: 'sku.id',
      fill: sku => ({
        sku,
        name: buildSkuName(sku),
      }),
    },
  },
  computer: {
    '': [
      computeAmount,
      value => {
        const total = R.reduce(
          (acc, { qty }) => acc + qty,
          0,
          value.items || [],
        )
        return { summary: `共 ${total} 個` }
      },
      computeEarnPoints,
    ],
    'items.0': value => {
      const qty = value.qty || 0
      const price = value.price || 0
      const discountRate = value.discountRate || 1

      return { subtotal: Math.round(qty * price * discountRate) }
    },
  },
  watcher: {
    '': { name: 'autoSelect' },
    'consumer.phone': { name: 'findConsumerByPhone' },
    mobileBarcode: { name: 'findConsumerByBarcode' },
    'items.0.sku.id': { name: 'findProductPrice' },
  },
  creator: {
    date: '$$NOW',
    status: '新增',
    'items.0.qty': 1,
    'items.0.discountRate': 1,
  },
  validator: {
    mobileBarcode: mobileBarcodeValidator,
    'consumer.phone': value => mobilePhoneValidator(value),
    discount: value => {
      if (Number.isInteger(value)) return null

      return '整單折扣為折扣一個整數金額'
    },
    // A 折抵 is how much to TAKE OFF, so a negative one has no meaning a
    // cashier would intend -- and once the sign is applied it would become a
    // GRANT of that many points. Blocking the save is right here, unlike the
    // over-balance warning, which is a dialog precisely because a stale
    // snapshot must never stop a sale.
    'spendPoints.value': spendable('折抵點數'),
    'spendCredits.value': spendable('折抵購物金'),
  },
  required: {
    'warehouse.id': true,
    'pricebook.id': true,
    payType: true,
    'items.0.qty': true,
    'items.0.price': true,
    'items.0.name': true,
    'items.0.amount': true,
  },
  disabled: {
    key: true,
    date: true,
    // Resolved by the phone lookup. Typed by hand it would credit whoever that
    // id happens to belong to.
    'consumer.members.shopline.vendorId': true,
    // status: true,
    summary: true,
    amount: true,
    // 'consumer.id': true,
    'warehouse.id': true,
    'pricebook.id': true,
    'items.0.bookPrice.price': true,
    'items.0.subtotal': true,
    'items.0.amount': true,
  },
  actions: {
    saveAndCreate: {
      authChecker: () => null,
    },
    saveAndCopy: {
      authChecker: () => null,
    },
  },
  effect: {
    autoSelect: autoSelect({
      pricebookKey: '台幣建議售價',
      warehouseKey: 'HH',
    }),
    findProductPrice,
    findConsumerByPhone: function* findConsumerByPhone(payload, ctx) {
      if (mobilePhoneValidator(payload.value) !== null) return

      // The consumer is linked once and the balances are re-read every time the
      // number is typed: the link is a decision, the balances are live. One
      // request answers both, and `linkConsumer` says which half to apply.
      yield ctx.S.call(
        lookupConsumer,
        {
          key: 'phone',
          value: payload.value,
          linkConsumer: !R.path(['consumer', 'id'], ctx.getDoc()),
        },
        ctx,
      )
    },
    findConsumerByBarcode: function* findConsumerByBarcode(payload, ctx) {
      if (R.path(['consumer', 'id'], ctx.getDoc())) return
      if (mobileBarcodeValidator(payload.value) !== null) return

      // A barcode means nothing to a loyalty platform, but the consumer it
      // identifies may already be linked to one -- so this answer DOES carry
      // members, resolved from that consumer's stored member link or from the
      // phone on their record. See consumers/entity/lookupConsumer: vendors are
      // asked for a consumer or a phone, whichever we have. They come back
      // empty only when the barcode matches no SINGLE consumer of ours, which
      // is both "nobody" and "more than one".
      //
      // So a scan that gets past the guard above can unlock the vendor section:
      // writeMembers applies whatever comes back, and the 折抵 boxes follow the
      // vendor id in it. Once a consumer is linked that guard returns early, so
      // rescanning re-asks nothing.
      yield ctx.S.call(
        lookupConsumer,
        {
          key: 'mobileBarcode',
          value: payload.value,
        },
        ctx,
      )
    },
    // The reload icon on the member id field. Balances only -- see
    // refreshBalances for what it deliberately leaves alone.
    reloadMemberBalances: function* reloadMemberBalances(payload, ctx) {
      yield ctx.S.call(refreshBalances, ctx)
    },
    saveAndPrintInvoice,
    saveAndPrintInvoiceOnPos,
    invalidInvoice: function* invalidInvoiceEffect(payload, ctx) {
      yield ctx.S.call(invalidInvoice, payload.id)
      yield ctx.S.call(editEffects.save)
    },
  },
  ui: {
    // The head form, grouped by the question each field answers rather than by
    // how it happened to fit in a grid. Rendered by Cashier.jsx: the outer
    // array is rows, so the two short sections share the last line, and no
    // width arithmetic decides where things wrap.
    //
    // Fields keep their original order inside a section; only the section a
    // field belongs to has changed. `payType` is not here -- the action row
    // owns it, next to the checkout button.
    head: [
      [
        {
          name: 'formEditor',
          title: '訂單資訊',
          cols: { default: 190, 6: 260 },
          fields: [
            [
              'key',
              'date',
              'status',
              'salesPerson',
              'pricebook.id',
              'warehouse.id',
              'note',
            ],
          ],
        },
      ],
      [
        {
          name: 'formEditor',
          title: '會員資訊',
          // A UNIFORM GRID, and 消費者 takes three columns of it. It holds three
          // values where the other two hold one -- it is a reference, and the
          // box shows name, phone and barcode joined -- so at the single column
          // the others use, all three were clipped. Spans rather than a width
          // on column 0 so the ratio is in the layout rather than arithmetic a
          // reader has to redo, the same way the vendor section below spans.
          cols: { default: 160 },
          fields: [
            [
              { path: 'consumer.id', span: 3 },
              'consumer.phone',
              'consumer.name',
            ],
          ],
        },
      ],
      [
        {
          // ONE SECTION PER VENDOR. Everything about a single loyalty platform
          // sits together: who the customer is there, and what this sale does
          // to their balances. A second vendor gets a second section beside
          // this one, and the index in these paths is what tells them apart --
          // which is also the answer to "which vendor is this 折抵 against".
          name: 'formEditor',
          title: 'SHOPLINE 資訊',
          // WHICH VENDOR THIS SECTION IS, named rather than inferred from the
          // paths below. Cashier.jsx asks whether this vendor resolved to a
          // member id before it lets the 折抵 boxes be typed into; guessing the
          // vendor from a field path would put another platform's answer to
          // that question on this section.
          vendor: SHOPLINE,
          // TWELVE NARROW COLUMNS, so the whole membership fits on one line.
          // The three amounts get a wider column each (indices 3, 6 and 9):
          // they hold a number plus the `/500` balance adornment, where a note
          // beside them only holds the few characters that survive the remark.
          cols: { default: 85, 3: 120, 6: 120, 9: 120 },
          // While the order is being typed: who the member is, and what this
          // sale should do to their balances.
          //
          // The balances have no field of their own. They are read-only numbers
          // that only mean anything next to the amount being spent, so they are
          // an adornment inside the spend field -- `[___ /500]` -- which is one
          // fewer column and one less thing to read.
          fields: [
            [
              // The icon lives inside the field, so it reads as belonging to
              // the member rather than to the row. Three columns is enough for
              // the 24-character id now that the vendor is in the section
              // title rather than prefixed onto the value.
              {
                path: 'consumer.members.shopline.vendorId',
                span: 3,
                endAdornment: reloadBalances,
              },
              { path: 'earnPoints.value', span: 1 },
              { path: 'earnPoints.note', span: 2 },
              {
                path: 'spendPoints.value',
                span: 1,
                endAdornment: pointsBalance(SHOPLINE),
              },
              { path: 'spendPoints.note', span: 2 },
              {
                path: 'spendCredits.value',
                span: 1,
                endAdornment: creditBalance(SHOPLINE),
              },
              { path: 'spendCredits.note', span: 2 },
            ],
          ],
          savedFields: [
            [
              {
                path: 'consumer.members.shopline.vendorId',
                span: 3,
                endAdornment: reloadBalances,
              },
            ],
          ],
        },
      ],
      [
        {
          name: 'formEditor',
          title: '發票資訊',
          cols: { default: 200 },
          fields: [['mobileBarcode', 'citizenBarcode', 'donation', 'buyerId']],
        },
        {
          name: 'formEditor',
          title: '金額資訊',
          // Styled by Cashier.jsx: these are the figures the staff reads out to
          // the customer, so they are set in bold red while the fields around
          // them stay plain.
          className: 'cashier-amounts',
          cols: { default: 190 },
          fields: [['summary', 'amount', 'discount']],
        },
      ],
    ],
    productReader: {
      url: '/api/route/querySku',
      queryKey: 'barcode',
      formatter: buildSkuName,
      initFocus: true,
      getQueryCode: c => c.toUpperCase(),
      buildItem: spec => {
        const { code, sku, nextQty } = spec

        const item = {
          barcodeOnObject: code,
          qty: nextQty,
          discountRate: 1,
        }
        if (sku) item.sku = { id: sku }

        return item
      },
      eqSku: () => false,
    },
    items: {
      name: 'tableEditor',
      path: 'items',
      fields: [
        { path: 'barcodeOnObject', width: 300 },
        { path: 'sku.id', width: 350 },
        { path: 'name', width: 350 },
        { path: 'qty', width: 50 },
        { path: 'price', width: 70 },
        { path: 'discountRate', width: 70 },
        { path: 'bookPrice.price', width: 70 },
        { path: 'subtotal', width: 70 },
        { path: 'amount', width: 70 },
      ],
      deletable: true,
      copyable: false,
    },
    invoices: {
      name: 'tableEditor',
      path: 'invoices',
      fields: [
        { path: 'invoice.yearmonth', label: '期別', width: 50 },
        { path: 'invoice.no', label: '號碼', width: 100 },
        { path: 'invoice.date', label: '日期', width: 200 },
        { path: 'invoice.amount.total', label: '金額', width: 70 },
      ],
      deletable: false,
      copyable: false,
      appendEmpty: false,
    },
  },
  Components: {
    PageForm: Cashier,
    PageActions: () => null,
  },
}
